import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { focusables } from '@shared/lib/appInert';
import { popIn } from '@shared/theme/animations';
import { IconX } from './icons';
import { createModalStack, nextFocusIndex } from './modalStack';

const backdropFade = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.z.modal};
  padding: ${({ theme }) => theme.spacing(4)};
  animation: ${backdropFade} ${({ theme }) => theme.motion.base}
    ${({ theme }) => theme.motion.easeOut};
`;

const Dialog = styled.div`
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.xl};
  padding: ${({ theme }) => theme.spacing(7)};
  /* min(480px, 100%) === 480px на любом вьюпорте ≥ 480; ниже — ужимается до
     ширины бэкдропа, чтобы на узких экранах (360px) не было горизонтального
     скролла. Поведение на десктопе не меняется. */
  min-width: min(480px, 100%);
  max-width: 720px;
  max-height: 90vh;
  overflow: auto;
  box-shadow: ${({ theme }) => theme.shadow.lg};
  animation: ${popIn} ${({ theme }) => theme.motion.slow} ${({ theme }) => theme.motion.spring};

  /* Фокус на самом окне (сразу после открытия) рамкой не обводим. */
  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.lg};
    border-radius: ${({ theme }) => theme.radius.xl};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const CloseBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: -4px -4px -4px 0;
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  transition:
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

interface Props {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}

interface OpenModal {
  close: () => void;
  panel: () => HTMLElement | null;
}

/**
 * Открытые окна по порядку открытия. Нажатия внутри окна разбирает само окно; сюда
 * доходят только нажатия, когда фокус вне окон (элемент с фокусом исчез и фокус упал на
 * страницу): Escape закрывает верхнее окно, Tab возвращает фокус в него.
 */
const openModals = createModalStack<OpenModal>();

function onDocumentKeyDown(e: globalThis.KeyboardEvent) {
  const top = openModals.top();
  if (!top || e.defaultPrevented) return;
  const active = document.activeElement;
  if (e.key === 'Escape') {
    // Фокус в другом модальном окне (Dialog, Drawer): этот Escape — его.
    if (active instanceof Element && active.closest('[aria-modal="true"]')) return;
    top.close();
  } else if (e.key === 'Tab' && (!active || active === document.body)) {
    const panel = top.panel();
    if (!panel) return;
    e.preventDefault();
    (focusables(panel)[0] ?? panel).focus();
  }
}

function pushModal(entry: OpenModal): () => void {
  if (openModals.size() === 0) document.addEventListener('keydown', onDocumentKeyDown);
  const remove = openModals.push(entry);
  return () => {
    remove();
    if (openModals.size() === 0) document.removeEventListener('keydown', onDocumentKeyDown);
  };
}

/**
 * Окно поверх страницы. Фокус при открытии уходит в окно и не выходит из него по Tab,
 * Escape закрывает только верхнее из открытых окон, после закрытия фокус возвращается
 * туда, откуда окно открыли. Окно рисуется на месте, без портала (кнопки внутри остаются
 * в форме вокруг окна), поэтому `inert` на страницу не ставится: он выключил бы и само окно.
 */
export function Modal({ open, ...props }: Props) {
  if (!open) return null;
  return <ModalPanel {...props} />;
}

function ModalPanel({ title, onClose, children }: Omit<Props, 'open'>) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  // Откуда открыли — при первой отрисовке: поле с autoFocus забирает фокус раньше эффектов.
  const [opener] = useState(() =>
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );
  // Нажали ли кнопку мыши на самой подложке: выделение текста в поле с отпусканием за окном
  // — тоже щелчок по подложке, но окно с введённым закрываться не должно.
  const pressedOnBackdrop = useRef(false);

  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
    const remove = pushModal({ close: () => closeRef.current(), panel: () => panelRef.current });
    return () => {
      remove();
      // Фокус — туда, откуда открыли, если его уже не забрало другое окно или поле.
      const active = document.activeElement;
      if (opener?.isConnected && opener !== document.body && (!active || active === document.body)) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [opener]);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    // Нажатие уже разобрал элемент внутри окна (выпадающий список закрылся по Escape).
    if (e.defaultPrevented) return;
    if (e.key === 'Escape') {
      // Только это окно: нижнее окно и страница этот Escape не получат.
      e.stopPropagation();
      closeRef.current();
      return;
    }
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    // Нажатие из выпадающего списка в портале: фокусом там управляет список.
    if (!panel || !(e.target instanceof Node) || !panel.contains(e.target)) return;
    const items = focusables(panel);
    if (items.length === 0) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const active = document.activeElement;
    const next = nextFocusIndex(
      items.length,
      active instanceof HTMLElement ? items.indexOf(active) : -1,
      e.shiftKey,
    );
    if (next !== null) {
      e.preventDefault();
      items[next].focus();
    }
  }

  return (
    <Backdrop
      onMouseDown={(e) => {
        pressedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        // Закрывает только щелчок, у которого и нажатие, и отпускание — на подложке.
        if (pressedOnBackdrop.current && e.target === e.currentTarget) onClose();
        pressedOnBackdrop.current = false;
      }}
    >
      <Dialog
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <Header>
          <Title id={titleId}>{title}</Title>
          <CloseBtn type="button" onClick={onClose} aria-label="Закрыть" title="Закрыть">
            <IconX size={18} />
          </CloseBtn>
        </Header>
        {children}
      </Dialog>
    </Backdrop>
  );
}
